import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import Model from 'flarum/common/Model';
import Discussion from 'flarum/common/models/Discussion';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import TextEditor from 'flarum/common/components/TextEditor';
import m from 'mithril';

// Globales flarum-Objekt (Extension-Registry), für Zugriff auf fof/upload-Komponenten
declare const flarum: any;

// Erzeugt ein sichtbares, eindeutiges Platzhalter-Zeichen pro Upload.
function makeMarker(index: number): string {
  return '[Bild ' + (index + 1) + ']';
}

// Extrahiert die Bild-URL aus einem fof/upload-Platzhalter:
// [upl-image-preview uuid=... url=https://... alt={TEXT?}]
function extractUrlFromPlaceholder(text: string): string {
  const match = text.match(/url=(?:"([^"]+)"|([^\s\]]+))/);
  return match ? (match[1] || match[2] || '') : '';
}

// Stellt sicher, dass das Overlay-Element existiert. Es wird bewusst NICHT ins
// Mithril-vdom gehängt (das würde den DOM-managed editorContainer/Textarea
// zerstören!), sondern als eigenes fixed-Element an document.body.
function ensureOverlay(editor: any): HTMLElement {
  if (!editor._uplOverlayEl || !document.body.contains(editor._uplOverlayEl)) {
    const el = document.createElement('div');
    el.className = 'UploadPreviewInline';
    document.body.appendChild(el);
    editor._uplOverlayEl = el;
  }
  return editor._uplOverlayEl;
}

// Positioniert die Thumbnail-Overlays direkt rechts neben den [Bild]-Markern.
// Berechnung über getBoundingClientRect der Textarea + Caret-Koordinaten,
// Positionierung mit position:fixed — unabhängig von Offset-Parents.
function repositionUploadThumbs(editor: any) {
  const overlay = ensureOverlay(editor);
  const driver = editor.attrs.composer && editor.attrs.composer.editor;
  if (!driver || !driver.el) return;

  // Overlay-Inhalt aus aktuellen Markern aufbauen (idempotent)
  overlay.innerHTML = '';
  const markers = (editor._uplMarkers || []).filter((item: any) => extractUrlFromPlaceholder(item.placeholder));
  if (!markers.length) {
    overlay.style.display = 'none';
    return;
  }
  overlay.style.display = 'block';

  markers.forEach((item: any) => {
    const img = document.createElement('img');
    img.dataset.marker = item.marker;
    img.src = extractUrlFromPlaceholder(item.placeholder);
    img.alt = '';
    overlay.appendChild(img);
  });

  const taRect = driver.el.getBoundingClientRect();
  const value = driver.el.value || '';
  let searchFrom = 0;

  markers.forEach((item: any) => {
    const img = overlay.querySelector('img[data-marker="' + item.marker + '"]') as HTMLElement;
    if (!img) return;

    const idx = value.indexOf(item.marker, searchFrom);
    if (idx === -1) {
      img.style.visibility = 'hidden';
      return;
    }
    searchFrom = idx + item.marker.length;

    // Koordinaten direkt NACH dem Marker (rechts daneben), relativ zur Textarea
    const coords = driver.getCaretCoordinates(idx + item.marker.length);
    img.style.left = taRect.left + coords.left + 'px';
    img.style.top = taRect.top + coords.top + 'px';
    img.style.visibility = 'visible';
  });
}

app.initializers.add('philippe-upload-preview', () => {
  Discussion.prototype.firstImageUrl = Model.attribute('firstImageUrl');

  // Thumbnail des ersten Bildes rechts im Diskussions-Listeneintrag anzeigen
  extend(DiscussionListItem.prototype, 'contentItems', function (items) {
    const url = this.attrs.discussion.firstImageUrl && this.attrs.discussion.firstImageUrl();

    if (!url) return;

    items.add(
      'thumb',
      <a className="DiscussionListItem-thumb" href={app.route.discussion(this.attrs.discussion)} aria-hidden="true" tabindex={-1}>
        <img src={url} alt="" loading="lazy" />
      </a>,
      60
    );
  });

  extend(TextEditor.prototype, 'oninit', function () {
    this._uplMarkers = this._uplMarkers || [];
  });

  // Composer: Thumbnail direkt neben den [Bild]-Platzhalter + Platzhalter-Text
  // beim Absenden wiederherstellen.
  extend(TextEditor.prototype, 'oncreate', function () {
    // Platzhalter verstecken + URL gewinnen: fof/upload ruft
    // editor.insertAtCursor("[upl-image-preview ...]\n") auf — sowohl vom
    // Upload-Button als auch vom Media-Manager („Bild einfügen", KEIN
    // success-Event). Daher holen wir die URL direkt aus dem Platzhalter.
    const editor = this.attrs.composer && this.attrs.composer.editor;
    if (editor && editor.insertAtCursor && !this._uplInsertWrapped) {
      this._uplInsertWrapped = true;
      const originalInsert = editor.insertAtCursor.bind(editor);

      editor.insertAtCursor = (text: string, escape: boolean) => {
        if (typeof text === 'string' && /^\[upl-(?:image|file|text)-preview/.test(text)) {
          const marker = makeMarker(this._uplMarkers.length);
          this._uplMarkers.push({ marker, placeholder: text });

          // Marker + Zeilenumbruch einfügen (fof/upload hängt "\n" an)
          return originalInsert(marker + '\n', escape);
        }
        return originalInsert(text, escape);
      };
    }

    // Overlay-Positionen bei Eingabe/Scroll/Resize aktuell halten
    const driver = this.attrs.composer && this.attrs.composer.editor;
    if (driver && driver.el && !this._uplRepositionBound) {
      this._uplRepositionBound = true;
      this._uplReposition = () => repositionUploadThumbs(this);
      driver.el.addEventListener('input', this._uplReposition);
      driver.el.addEventListener('scroll', this._uplReposition);
      driver.el.addEventListener('keyup', this._uplReposition);
      window.addEventListener('resize', this._uplReposition);
      window.addEventListener('scroll', this._uplReposition, true);
    }

    // Erstmalig positionieren (nachdem das Overlay im DOM ist)
    repositionUploadThumbs(this);
  });

  // Nach jedem Redraw (Upload) Positionen neu berechnen
  extend(TextEditor.prototype, 'onupdate', function () {
    repositionUploadThumbs(this);
  });

  // Overlay aufräumen, wenn der Editor zerstört wird
  extend(TextEditor.prototype, 'onremove', function () {
    if (this._uplOverlayEl && document.body.contains(this._uplOverlayEl)) {
      this._uplOverlayEl.remove();
    }
    this._uplOverlayEl = null;

    const driver = this.attrs.composer && this.attrs.composer.editor;
    if (driver && driver.el && this._uplReposition) {
      driver.el.removeEventListener('input', this._uplReposition);
      driver.el.removeEventListener('scroll', this._uplReposition);
      driver.el.removeEventListener('keyup', this._uplReposition);
      window.removeEventListener('resize', this._uplReposition);
      window.removeEventListener('scroll', this._uplReposition, true);
    }
  });

  // Beim Absenden: [Bild]-Marker wieder durch die echten Platzhalter ersetzen,
  // damit der Beitrag korrekt gerendert wird (Thumbnail an der Einfügeposition).
  override(TextEditor.prototype, 'onsubmit', function (original) {
    if (this._uplMarkers && this._uplMarkers.length) {
      let value = this.value || '';
      for (const { marker, placeholder } of this._uplMarkers) {
        // Zuerst "Marker\n" ersetzen (fof/upload fügt Platzhalter mit \n ein),
        // danach übrig gebliebene Marker ohne Umbruch (User hat \n gelöscht)
        value = value.split(marker + '\n').join(placeholder);
        value = value.split(marker).join(placeholder);
      }
      this.value = value;

      // Stream synchron halten (DiscussionComposer/ReplyComposer lesen den
      // Inhalt aus composer.fields.content())
      if (typeof this.attrs.onchange === 'function') {
        this.attrs.onchange(value);
      }

      this._uplMarkers = [];
    }

    return original();
  });

  // Media Manager (fof/upload FileManagerModal): Die zwei Buttons aus dem
  // Modal-footer (Abbrechen + „Auswahl bestätigen") nach ganz oben in die
  // fof-modal-buttons-Leiste verschieben. Das Modal ist komplett
  // Mithril-gerendert (keine DOM-managed children wie beim TextEditor), der
  // vnode-Baum wird vor dem Rendern umsortiert — sicher.
  const fofUpload = flarum.extensions && flarum.extensions['fof-upload'];
  const FileManagerModal = fofUpload && fofUpload.components && fofUpload.components.FileManagerModal;

  if (FileManagerModal) {
    extend(FileManagerModal.prototype, 'view', function (this: any, vnode: any) {
      if (!vnode) return;

      // Root-vnode: <div className="Modal modal-dialog ...">
      const rootChildren = Array.isArray(vnode.children) ? vnode.children : vnode.children ? [vnode.children] : [];
      const content = rootChildren.find(
        (c: any) => c && c.attrs && typeof c.attrs.className === 'string' && c.attrs.className.indexOf('Modal-content') !== -1
      );
      if (!content || !Array.isArray(content.children)) return;

      const contentChildren = content.children;

      const footerIdx = contentChildren.findIndex((c: any) => c && c.attrs && c.attrs.className === 'Modal-footer');
      const topBarIdx = contentChildren.findIndex(
        (c: any) => c && c.attrs && typeof c.attrs.className === 'string' && c.attrs.className.indexOf('fof-modal-buttons') !== -1
      );
      if (footerIdx === -1 || topBarIdx === -1) return;

      const footer = contentChildren[footerIdx];
      if (!footer || !Array.isArray(footer.children)) return;

      // Buttons (Abbrechen + Auswahl bestätigen) aus dem Footer holen,
      // Footer entfernen, Buttons oben in die fof-modal-buttons-Leiste hängen
      const buttons = footer.children;
      contentChildren.splice(footerIdx, 1);

      const topBar = contentChildren[topBarIdx];
      const topBarChildren = (Array.isArray(topBar.children) ? topBar.children : topBar.children ? [topBar.children] : []).filter(Boolean);
      topBar.children = topBarChildren.concat(buttons);
      // App-backControl entfernen: Die Theme-Regeln (.App-backControl>.Button .Button-label{display:none},
      // transparenter Hintergrund, Header-Höhe) würden die verschobenen Buttons unsichtbar machen.
      topBar.attrs.className = 'fof-modal-buttons fof-upload-top-actions';
    });
  }
});
